/**
 * Buyer Requests Management — مدیریت درخواست‌های خرید (مارکت‌پلیس معکوس)
 *
 * صف بررسی درخواست‌های خریداران: تأیید و انتشار، رد با دلیل، بستن اجباری،
 * مشاهدهٔ جزئیات کامل + پیشنهادهای فروشندگان + سفارش مرتبط.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
} from '@mui/material';
import moment from 'moment-jalaali';
import { useSnackbar } from 'notistack';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BlockIcon from '@mui/icons-material/Block';
import InboxIcon from '@mui/icons-material/Inbox';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ReceiptIcon from '@mui/icons-material/Receipt';
import GavelIcon from '@mui/icons-material/Gavel';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { toPersianDigits } from '../components/common/JalaliDatePicker';
import buyerRequestService, {
  BuyerRequestItem,
  BuyerRequestDetail,
  BuyerRequestStatus,
} from '../services/buyerRequest.service';

import './EntityManagement.css';
import './BuyerRequestsManagement.css';

// ---------------------------------------------------------------------------
// Labels & helpers
// ---------------------------------------------------------------------------

const statusLabels: Record<string, string> = {
  pending_review: 'در انتظار بررسی',
  active: 'منتشرشده',
  offer_accepted: 'پیشنهاد پذیرفته‌شده',
  converted: 'تبدیل به سفارش',
  expired: 'منقضی',
  cancelled: 'لغوشده',
  rejected: 'ردشده',
};

const offerStatusLabels: Record<string, string> = {
  active: 'فعال',
  withdrawn: 'پس‌گرفته',
  accepted: 'پذیرفته‌شده',
  declined: 'ردشده',
  released: 'آزادشده',
  expired: 'منقضی',
  completed: 'تکمیل‌شده',
};

/** ترتیب تب‌های وضعیت — پیش‌فرض «در انتظار بررسی» */
const STATUS_TABS: { value: string; label: string }[] = [
  { value: 'pending_review', label: 'در انتظار بررسی' },
  { value: '', label: 'همه' },
  { value: 'active', label: 'منتشرشده' },
  { value: 'offer_accepted', label: 'پیشنهاد پذیرفته‌شده' },
  { value: 'converted', label: 'تبدیل به سفارش' },
  { value: 'expired', label: 'منقضی' },
  { value: 'cancelled', label: 'لغوشده' },
  { value: 'rejected', label: 'ردشده' },
];

const formatToman = (n?: number | null): string =>
  typeof n === 'number' && !Number.isNaN(n) ? new Intl.NumberFormat('fa-IR').format(n) : '-';

const formatJalali = (iso?: string | null): string => {
  if (!iso) return '-';
  try { return toPersianDigits(moment(iso).format('jYYYY/jMM/jDD HH:mm')); }
  catch { return '-'; }
};

const formatJalaliShort = (iso?: string | null): string => {
  if (!iso) return '-';
  try { return toPersianDigits(moment(iso).format('jYYYY/jMM/jDD')); }
  catch { return '-'; }
};

/** نمایش بازهٔ بودجه به تومان */
const formatBudget = (min?: number, max?: number): string => {
  const hasMin = typeof min === 'number' && min > 0;
  const hasMax = typeof max === 'number' && max > 0;
  if (hasMin && hasMax) return `${formatToman(min)} تا ${formatToman(max)} تومان`;
  if (hasMin) return `از ${formatToman(min)} تومان`;
  if (hasMax) return `تا ${formatToman(max)} تومان`;
  return '-';
};

const asObj = (v: any): any => (v && typeof v === 'object' ? v : null);

/** مقدار فیلد داینامیک را برای نمایش انسانی تبدیل می‌کند */
const renderFieldValue = (v: any): string => {
  if (v === null || v === undefined || v === '') return '-';
  if (typeof v === 'boolean') return v ? 'بله' : 'خیر';
  if (Array.isArray(v)) return v.map(renderFieldValue).join('، ');
  if (typeof v === 'object') {
    if (typeof v.label === 'string') return v.label;
    if (typeof v.name === 'string') return v.name;
    if (typeof v.value !== 'undefined') return renderFieldValue(v.value);
    return JSON.stringify(v);
  }
  if (typeof v === 'number') return toPersianDigits(String(v));
  return String(v);
};

type ActionType = 'approve' | 'reject' | 'close';

interface PendingAction {
  type: ActionType;
  id: string;
  title: string;
}

const actionMeta: Record<ActionType, { title: string; confirmLabel: string; color: 'success' | 'error' | 'warning' }> = {
  approve: { title: 'تأیید و انتشار درخواست', confirmLabel: 'تأیید و انتشار', color: 'success' },
  reject: { title: 'رد درخواست', confirmLabel: 'رد درخواست', color: 'error' },
  close: { title: 'بستن درخواست', confirmLabel: 'بستن درخواست', color: 'warning' },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const BuyerRequestsManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // List state
  const [items, setItems] = useState<BuyerRequestItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState<string>('pending_review'); // پیش‌فرض: صف بررسی
  const [pendingCount, setPendingCount] = useState<number | null>(null);

  // Detail modal
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<BuyerRequestDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Action dialog
  const [action, setAction] = useState<PendingAction | null>(null);
  const [actionReason, setActionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await buyerRequestService.getRequests({
        page,
        limit,
        search: search.trim() || undefined,
        status: status || undefined,
      });
      setItems(res.items);
      setTotal(res.meta.totalItems);
      if (status === 'pending_review' && !search.trim()) {
        setPendingCount(res.meta.totalItems);
      }
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در دریافت لیست درخواست‌ها', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, status, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  // شمارندهٔ صف «در انتظار بررسی» برای بَج تب — جدا از لیست جاری
  const refreshPendingCount = useCallback(async () => {
    try {
      const res = await buyerRequestService.getRequests({ status: 'pending_review', page: 1, limit: 1 });
      setPendingCount(res.meta.totalItems);
    } catch {
      /* بَج اختیاری است؛ خطا را نادیده می‌گیریم */
    }
  }, []);

  useEffect(() => { refreshPendingCount(); }, [refreshPendingCount]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch();
  };

  const selectStatus = (value: string) => {
    setStatus(value);
    setPage(1);
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  // --- Detail modal -----------------------------------------------------
  const openDetail = useCallback(async (id: string) => {
    setDetailId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const d = await buyerRequestService.getRequestDetail(id);
      setDetail(d);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در دریافت جزئیات درخواست', { variant: 'error' });
      setDetailId(null);
    } finally {
      setDetailLoading(false);
    }
  }, [enqueueSnackbar]);

  const closeDetail = () => {
    setDetailId(null);
    setDetail(null);
  };

  // --- Actions ----------------------------------------------------------
  const startAction = (type: ActionType, row: { _id: string; title: string }) => {
    setAction({ type, id: row._id, title: row.title });
    setActionReason('');
  };

  const runAction = async () => {
    if (!action) return;
    if (action.type === 'reject' && !actionReason.trim()) {
      enqueueSnackbar('نوشتن دلیل رد الزامی است', { variant: 'warning' });
      return;
    }
    setActionLoading(true);
    try {
      if (action.type === 'approve') {
        await buyerRequestService.approve(action.id);
        enqueueSnackbar('درخواست تأیید و برای ۷ روز منتشر شد', { variant: 'success' });
      } else if (action.type === 'reject') {
        await buyerRequestService.reject(action.id, actionReason.trim());
        enqueueSnackbar('درخواست رد شد', { variant: 'success' });
      } else {
        await buyerRequestService.close(action.id, actionReason.trim() || undefined);
        enqueueSnackbar('درخواست بسته شد', { variant: 'success' });
      }
      setAction(null);
      setActionReason('');
      await load();
      refreshPendingCount();
      // اگر مودال جزئیاتِ همین درخواست باز است، تازه‌سازی کن
      if (detailId === action.id) {
        openDetail(action.id);
      }
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در انجام عملیات', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const canApprove = (s: BuyerRequestStatus) => s === 'pending_review';
  const canReject = (s: BuyerRequestStatus) => s === 'pending_review';
  const canClose = (s: BuyerRequestStatus) => s === 'pending_review' || s === 'active';

  // --- Renders ------------------------------------------------------------

  const renderStatusBadge = (s: string) => (
    <span className={`br-status-badge ${s}`}>{statusLabels[s] || s}</span>
  );

  const renderRowActions = (row: BuyerRequestItem, withLabels = false) => (
    <>
      <Tooltip title="جزئیات کامل" arrow>
        <button className="br-action-btn" onClick={() => openDetail(row._id)}>
          <InfoOutlinedIcon fontSize="small" /> {withLabels && 'جزئیات'}
        </button>
      </Tooltip>
      {canApprove(row.status) && (
        <Tooltip title="تأیید و انتشار" arrow>
          <button className="br-action-btn approve" disabled={loading} onClick={() => startAction('approve', row)}>
            <CheckCircleIcon fontSize="small" /> {withLabels && 'تأیید'}
          </button>
        </Tooltip>
      )}
      {canReject(row.status) && (
        <Tooltip title="رد درخواست" arrow>
          <button className="br-action-btn reject" disabled={loading} onClick={() => startAction('reject', row)}>
            <CancelIcon fontSize="small" /> {withLabels && 'رد'}
          </button>
        </Tooltip>
      )}
      {canClose(row.status) && (
        <Tooltip title="بستن درخواست" arrow>
          <button className="br-action-btn close" disabled={loading} onClick={() => startAction('close', row)}>
            <BlockIcon fontSize="small" /> {withLabels && 'بستن'}
          </button>
        </Tooltip>
      )}
    </>
  );

  const detailBuyer = asObj(detail?.userId);
  const detailAt = asObj(detail?.accountTypeId);
  const detailCat = asObj(detail?.categoryId);
  const detailReviewer = asObj(detail?.reviewedBy);
  const detailOrder = asObj(detail?.orderId);
  const detailFields = detail?.fields && typeof detail.fields === 'object'
    ? Object.entries(detail.fields)
    : [];

  return (
    <div className="buyer-requests-page">
      {/* Header */}
      <header className="br-header">
        <h1><RequestQuoteIcon /> درخواست‌های خرید</h1>
        <div className="header-actions">
          {loading && <CircularProgress size={20} />}
          <button className="header-btn refresh" onClick={() => { load(); refreshPendingCount(); }} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </header>

      {/* Status tabs */}
      <div className="br-status-tabs">
        {STATUS_TABS.map(tab => (
          <button
            key={tab.value || 'all'}
            className={`br-status-tab ${status === tab.value ? 'selected' : ''}`}
            onClick={() => selectStatus(tab.value)}
          >
            {tab.label}
            {tab.value === 'pending_review' && pendingCount !== null && pendingCount > 0 && (
              <span className="tab-count">{toPersianDigits(String(pendingCount))}</span>
            )}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group wide">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجو در عنوان/توضیحات..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={onSearchKey}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>
          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {!!search && (
              <button className="filter-btn outline" onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}>
                پاک
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table — Desktop */}
      <div className="br-table-wrapper">
        <table className="br-table">
          <thead>
            <tr>
              <th>عنوان</th>
              <th>خریدار</th>
              <th>نوع محصول</th>
              <th>بودجه</th>
              <th>تعداد</th>
              <th>پیشنهادها</th>
              <th>وضعیت</th>
              <th>تاریخ ثبت</th>
              <th>اقدامات</th>
            </tr>
          </thead>
          <tbody>
            {loading && !items.length ? (
              <tr>
                <td colSpan={9}>
                  <div className="loading-overlay"><CircularProgress size={30} /></div>
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={9}>
                  <div className="empty-state">
                    <InboxIcon />
                    <p>درخواستی یافت نشد</p>
                  </div>
                </td>
              </tr>
            ) : items.map(row => {
              const buyer = asObj(row.userId);
              const at = asObj(row.accountTypeId);
              return (
                <tr key={row._id}>
                  <td>
                    <div className="br-title-cell">
                      <div className="br-title-text">{row.title || '-'}</div>
                      {row.description && <div className="br-title-sub">{row.description}</div>}
                    </div>
                  </td>
                  <td>
                    <div className="br-buyer">
                      <span className="br-buyer-name">{buyer?.name || '-'}</span>
                      {buyer?.phone && <span className="br-buyer-phone">{toPersianDigits(buyer.phone)}</span>}
                    </div>
                  </td>
                  <td>
                    <div className="br-at">
                      {at?.image && <img src={at.image} alt="" />}
                      <span>{at?.name || '-'}</span>
                    </div>
                  </td>
                  <td>{formatBudget(row.budgetMin, row.budgetMax)}</td>
                  <td>{toPersianDigits(String(row.quantity || 1))}</td>
                  <td>
                    <div className="br-offers">
                      <span>
                        <LocalOfferIcon style={{ fontSize: 13, verticalAlign: 'middle', marginLeft: 4 }} />
                        {toPersianDigits(String(row.offersCount || 0))} پیشنهاد
                      </span>
                      {typeof row.lowestOfferPrice === 'number' && row.lowestOfferPrice > 0 && (
                        <span className="lowest">کمترین: {formatToman(row.lowestOfferPrice)} تومان</span>
                      )}
                    </div>
                  </td>
                  <td>{renderStatusBadge(row.status)}</td>
                  <td>{formatJalaliShort(row.createdAt)}</td>
                  <td>
                    <div className="br-actions-cell">{renderRowActions(row)}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Cards — Mobile */}
      <div className="br-cards">
        {items.map(row => {
          const buyer = asObj(row.userId);
          const at = asObj(row.accountTypeId);
          return (
            <div key={row._id} className="br-card">
              <div className="br-card-header">
                <div>
                  <div className="br-card-title">{row.title || '-'}</div>
                  <div className="br-card-buyer">
                    {buyer?.name || '-'}{buyer?.phone ? ` — ${toPersianDigits(buyer.phone)}` : ''}
                  </div>
                </div>
                {renderStatusBadge(row.status)}
              </div>
              <div className="br-card-row">
                <span className="br-card-label">نوع محصول:</span>
                <span className="br-card-value">{at?.name || '-'}</span>
              </div>
              <div className="br-card-row">
                <span className="br-card-label">بودجه:</span>
                <span className="br-card-value">{formatBudget(row.budgetMin, row.budgetMax)}</span>
              </div>
              <div className="br-card-row">
                <span className="br-card-label">پیشنهادها:</span>
                <span className="br-card-value">
                  {toPersianDigits(String(row.offersCount || 0))} پیشنهاد
                  {typeof row.lowestOfferPrice === 'number' && row.lowestOfferPrice > 0
                    ? ` | کمترین ${formatToman(row.lowestOfferPrice)} تومان`
                    : ''}
                </span>
              </div>
              <div className="br-card-row">
                <span className="br-card-label">تاریخ ثبت:</span>
                <span className="br-card-value">{formatJalaliShort(row.createdAt)}</span>
              </div>
              <div className="br-card-actions">{renderRowActions(row, true)}</div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      <div className="br-pagination">
        <span className="pagination-info">
          صفحه {toPersianDigits(String(page))} از {toPersianDigits(String(totalPages))} — مجموع {toPersianDigits(String(total))} مورد
        </span>
        <div className="pagination-btns">
          <button className="pagination-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            قبلی
          </button>
          <button className="pagination-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            بعدی
          </button>
        </div>
      </div>

      {/* ===== Detail Modal ===== */}
      <Dialog open={!!detailId} onClose={closeDetail} maxWidth="md" fullWidth dir="rtl">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 700 }}>
            <RequestQuoteIcon sx={{ color: '#B25373' }} />
            جزئیات درخواست خرید
          </span>
          <IconButton size="small" onClick={closeDetail}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers className="br-detail">
          {detailLoading || !detail ? (
            <div className="loading-overlay"><CircularProgress size={30} /></div>
          ) : (
            <>
              {/* اطلاعات اصلی */}
              <div className="br-detail-section">
                <h3><DescriptionIcon /> اطلاعات درخواست</h3>
                <div className="br-detail-grid">
                  <div className="br-detail-item"><span className="k">عنوان</span><span className="v">{detail.title || '-'}</span></div>
                  <div className="br-detail-item"><span className="k">وضعیت</span><span className="v">{renderStatusBadge(detail.status)}</span></div>
                  <div className="br-detail-item"><span className="k">نوع محصول</span><span className="v">{detailAt?.name || '-'}</span></div>
                  <div className="br-detail-item"><span className="k">دسته‌بندی</span><span className="v">{detailCat?.name || '-'}</span></div>
                  <div className="br-detail-item"><span className="k">بودجه</span><span className="v">{formatBudget(detail.budgetMin, detail.budgetMax)}</span></div>
                  <div className="br-detail-item"><span className="k">تعداد</span><span className="v">{toPersianDigits(String(detail.quantity || 1))}</span></div>
                  <div className="br-detail-item"><span className="k">بازدید</span><span className="v">{toPersianDigits(String(detail.views || 0))}</span></div>
                  <div className="br-detail-item"><span className="k">تاریخ ثبت</span><span className="v">{formatJalali(detail.createdAt)}</span></div>
                  <div className="br-detail-item"><span className="k">انقضا</span><span className="v">{formatJalali(detail.expiresAt)}</span></div>
                  {detail.paymentDeadline && (
                    <div className="br-detail-item"><span className="k">مهلت پرداخت</span><span className="v">{formatJalali(detail.paymentDeadline)}</span></div>
                  )}
                </div>
              </div>

              {/* توضیحات */}
              {detail.description && (
                <div className="br-detail-section">
                  <h3><DescriptionIcon /> توضیحات خریدار</h3>
                  <div className="br-detail-desc">{detail.description}</div>
                </div>
              )}

              {/* فیلدهای داینامیک */}
              {detailFields.length > 0 && (
                <div className="br-detail-section">
                  <h3><ListAltIcon /> مشخصات درخواستی</h3>
                  <div className="br-detail-grid">
                    {detailFields.map(([key, value]) => (
                      <div key={key} className="br-detail-item">
                        <span className="k">{key}</span>
                        <span className="v">{renderFieldValue(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* خریدار */}
              <div className="br-detail-section">
                <h3><PersonIcon /> خریدار</h3>
                <div className="br-detail-grid">
                  <div className="br-detail-item"><span className="k">نام</span><span className="v">{detailBuyer?.name || '-'}</span></div>
                  <div className="br-detail-item">
                    <span className="k">موبایل</span>
                    <span className="v" style={{ direction: 'ltr', textAlign: 'right' }}>
                      {detailBuyer?.phone ? toPersianDigits(detailBuyer.phone) : '-'}
                    </span>
                  </div>
                  <div className="br-detail-item"><span className="k">ایمیل</span><span className="v">{detailBuyer?.email || '-'}</span></div>
                </div>
              </div>

              {/* اطلاعات بررسی */}
              {(detailReviewer || detail.reviewedAt || detail.rejectionReason) && (
                <div className="br-detail-section">
                  <h3><GavelIcon /> بررسی ادمین</h3>
                  <div className="br-detail-grid" style={{ marginBottom: detail.rejectionReason ? 10 : 0 }}>
                    <div className="br-detail-item"><span className="k">بررسی‌کننده</span><span className="v">{detailReviewer?.name || '-'}</span></div>
                    <div className="br-detail-item"><span className="k">تاریخ بررسی</span><span className="v">{formatJalali(detail.reviewedAt)}</span></div>
                  </div>
                  {detail.rejectionReason && (
                    <div className="br-reject-reason">دلیل رد: {detail.rejectionReason}</div>
                  )}
                </div>
              )}

              {/* سفارش مرتبط */}
              {detailOrder && (
                <div className="br-detail-section">
                  <h3><ReceiptIcon /> سفارش مرتبط</h3>
                  <div className="br-detail-grid">
                    <div className="br-detail-item"><span className="k">شماره سفارش</span><span className="v">{detailOrder.orderNumber || '-'}</span></div>
                    <div className="br-detail-item"><span className="k">وضعیت سفارش</span><span className="v">{detailOrder.status || '-'}</span></div>
                    <div className="br-detail-item"><span className="k">مبلغ کل</span><span className="v">{formatToman(detailOrder.totalAmount)} تومان</span></div>
                  </div>
                </div>
              )}

              {/* پیشنهادهای فروشندگان */}
              <div className="br-detail-section">
                <h3><StorefrontIcon /> پیشنهادهای فروشندگان ({toPersianDigits(String(detail.offers?.length || 0))})</h3>
                {!detail.offers || detail.offers.length === 0 ? (
                  <div className="empty-state" style={{ padding: '24px 16px' }}>
                    <p>هنوز پیشنهادی ثبت نشده است</p>
                  </div>
                ) : (
                  <div className="br-offers-table-wrap">
                    <table className="br-offers-table">
                      <thead>
                        <tr>
                          <th>فروشگاه</th>
                          <th>فروشنده</th>
                          <th>قیمت</th>
                          <th>زمان تحویل</th>
                          <th>موجودی</th>
                          <th>وضعیت</th>
                          <th>تاریخ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.offers.map(offer => {
                          const store = asObj(offer.storeId);
                          const seller = asObj(offer.sellerUserId);
                          return (
                            <tr key={offer._id}>
                              <td>
                                <div className="br-at">
                                  {store?.logo && <img src={store.logo} alt="" />}
                                  <span>{store?.name || '-'}</span>
                                </div>
                              </td>
                              <td>
                                <div className="br-buyer">
                                  <span className="br-buyer-name">{seller?.name || '-'}</span>
                                  {seller?.phone && <span className="br-buyer-phone">{toPersianDigits(seller.phone)}</span>}
                                </div>
                              </td>
                              <td><strong>{formatToman(offer.price)}</strong> تومان</td>
                              <td>{typeof offer.deliveryHours === 'number' ? `${toPersianDigits(String(offer.deliveryHours))} ساعت` : '-'}</td>
                              <td>{offer.availability || '-'}</td>
                              <td><span className={`br-offer-badge ${offer.status}`}>{offerStatusLabels[offer.status] || offer.status}</span></td>
                              <td>{formatJalaliShort(offer.createdAt)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* اقدامات مودال */}
              {(canApprove(detail.status) || canReject(detail.status) || canClose(detail.status)) && (
                <div className="br-modal-actions">
                  {canApprove(detail.status) && (
                    <button className="br-modal-btn approve" onClick={() => startAction('approve', detail)}>
                      <CheckCircleIcon fontSize="small" /> تأیید و انتشار
                    </button>
                  )}
                  {canReject(detail.status) && (
                    <button className="br-modal-btn reject" onClick={() => startAction('reject', detail)}>
                      <CancelIcon fontSize="small" /> رد درخواست
                    </button>
                  )}
                  {canClose(detail.status) && (
                    <button className="br-modal-btn close-req" onClick={() => startAction('close', detail)}>
                      <BlockIcon fontSize="small" /> بستن درخواست
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Action Confirm Dialog ===== */}
      <Dialog open={!!action} onClose={() => !actionLoading && setAction(null)} maxWidth="xs" fullWidth dir="rtl">
        {action && (
          <>
            <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>
              {actionMeta[action.type].title}
            </DialogTitle>
            <DialogContent>
              <p style={{ fontSize: 13, margin: '4px 0 12px', lineHeight: 1.9 }}>
                {action.type === 'approve' && (
                  <>درخواست «{action.title}» تأیید و به مدت ۷ روز برای فروشندگان منتشر می‌شود. ادامه می‌دهید؟</>
                )}
                {action.type === 'reject' && (
                  <>درخواست «{action.title}» رد می‌شود و خریدار دلیل آن را می‌بیند. لطفاً دلیل رد را بنویسید:</>
                )}
                {action.type === 'close' && (
                  <>درخواست «{action.title}» به‌صورت اجباری بسته می‌شود. در صورت تمایل دلیل را بنویسید (اختیاری):</>
                )}
              </p>
              {(action.type === 'reject' || action.type === 'close') && (
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  size="small"
                  autoFocus
                  placeholder={action.type === 'reject' ? 'دلیل رد (الزامی)...' : 'دلیل بستن (اختیاری)...'}
                  value={actionReason}
                  onChange={e => setActionReason(e.target.value)}
                  error={action.type === 'reject' && !actionReason.trim()}
                  helperText={action.type === 'reject' && !actionReason.trim() ? 'نوشتن دلیل رد الزامی است' : undefined}
                />
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
              <Button onClick={() => setAction(null)} disabled={actionLoading} color="inherit" size="small">
                انصراف
              </Button>
              <Button
                onClick={runAction}
                disabled={actionLoading || (action.type === 'reject' && !actionReason.trim())}
                variant="contained"
                color={actionMeta[action.type].color}
                size="small"
                startIcon={actionLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
              >
                {actionMeta[action.type].confirmLabel}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default BuyerRequestsManagement;
